import { Button, Modal, Select, Spin, message } from 'antd';
import { ScanOutlined } from '@ant-design/icons';
import { getProductList } from 'apis/pms';
import UmsMemberSelect from 'components/ums-member-select';
import BarcodeScanner from 'components/barcode-scanner';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useToggle } from 'react-use';
import { PmsProductWithPriceType, PmsProduct } from 'types/pms';
import ProductTable from './product-table';
import {
  CHECKOUT_OUT_PRODUCT_PRICE_TYPE,
  findLabelByValue,
  PMS_PRODUCT_STOCK_STATUS,
  PMS_PRODUCT_STOCK_STATUS_OPTION_LIST,
} from 'commons/options';
import { produce } from 'immer';
import PaymentInfo from './payment-info';
import useCurrentShopStaffs from 'commons/hooks/useCurrentShopStaffs';
import PayloadContextProvider from './utils/payload-context';
import { usePayloadContext } from './utils/payload-context';
import { ActionType } from './utils/payload-reducer';
import { useAppSelector } from 'store/hooks';
import { selectUserInfo } from 'store/slices/userInfoSlice';
import i18n from '../../../i18n';
import LOCALS from '../../../commons/locals';
import { thousands } from 'utils/tools';
import { debounce, throttle } from 'lodash-es';
import { getStockPlaceByStoreId } from 'utils/store-id-and-stock-place';
import { StoreConfirmOrder } from 'types/oms';
import connectToPosPrinter, {
  posPrinterSetting,
} from 'utils/connect-to-pos-printer';
import { STOCK_PLACE_OPTION_LIST } from 'constant/stock-place';
import { ORDER_CREATE_SOURCE_MAP } from 'constant/order-create-source';
import { PUBLISH_STATUS_OPTION_LIST } from 'constant/pms-product';
import { SHOP_OPTION_LIST } from 'constant/shop';

const CHECKOUT_COUNTER_CACHE_SHOP_ID_KEY = 'CHECKOUT_COUNTER_CACHE_SHOP_ID_KEY';

const resolveScannedProduct = (keyword: string, list: PmsProduct[]) => {
  const trimmed = keyword.trim();
  if (!trimmed) return undefined;

  const productId = Number(trimmed);
  if (!Number.isNaN(productId)) {
    const byId = list.find((i) => i.id === productId);
    if (byId) return byId;
  }

  const bySn = list.find((i) => i.productSn === trimmed);
  if (bySn) return bySn;

  return list.length === 1 ? list[0] : undefined;
};

// 处理产地
const handleProductPriceType = (
  product: PmsProduct,
): PmsProductWithPriceType => {
  return {
    ...product,
    priceType: CHECKOUT_OUT_PRODUCT_PRICE_TYPE.WITH_TAX,
    quantity: 1,
  };
};

const OmsCheckoutCounter = () => {
  const [productList, setProductList] = useState<PmsProductWithPriceType[]>([]);
  const [loadingProductList] = useToggle(false);
  const staffSelectOptions = useCurrentShopStaffs();
  const { nickName, shop, subShop } = useAppSelector(selectUserInfo);
  const { dispatch, state } = usePayloadContext();
  // 合计金额集合
  const [orderResult, setOrderResult] = useState<
    StoreConfirmOrder | undefined
  >();

  const { createdFrom } = state;

  const allShopIdList = useMemo(() => {
    return [shop, ...subShop].filter((i) => i !== undefined);
  }, [shop, subShop]);

  useEffect(() => {
    if (posPrinterSetting.communicationType.get() === 'socket') {
      connectToPosPrinter().catch((err) => {
        console.log(err);
      });
    }
  }, []);

  // 改变payload参数
  useEffect(() => {
    const list = [...productList];
    const products = list.map((d) => {
      const {
        id,
        priceType,
        stockPlace,
        productSn,
        name,
        brandName,
        price,
        currency,
        quantity,
      } = d;

      return {
        id,
        isTaxFree:
          priceType === CHECKOUT_OUT_PRODUCT_PRICE_TYPE.WITH_TAX ? 0 : 1,
        stockPlace,
        productSn,
        name,
        brandName,
        price,
        currency,
        quantity,
      };
    });
    dispatch({
      type: ActionType.UPDATE_BATCH,
      payload: {
        productList: products,
      },
    });
  }, [productList, dispatch]);

  useEffect(() => {
    if (nickName) {
      const cachedShopIdStr = localStorage.getItem(
        CHECKOUT_COUNTER_CACHE_SHOP_ID_KEY,
      );
      const cachedShopId = cachedShopIdStr ? parseInt(cachedShopIdStr) : null;

      if (
        cachedShopId &&
        (allShopIdList.includes(cachedShopId) || allShopIdList.length === 0)
      ) {
        dispatch({
          type: ActionType.UPDATE_BATCH,
          payload: {
            staffName: nickName,
            createdFrom: cachedShopId,
          },
        });
        return;
      }

      dispatch({
        type: ActionType.UPDATE_BATCH,
        payload: {
          staffName: nickName,
          createdFrom: shop || ORDER_CREATE_SOURCE_MAP.GINZA,
        },
      });
    }
  }, [allShopIdList, dispatch, nickName, shop]);

  const [loading, toggleLoading] = useToggle(false);
  const [productScannerOpen, toggleProductScannerOpen] = useToggle(false);
  const [productOptionList, setProductOptionList] = useState<PmsProduct[]>([]);

  const getProductSearchParams = useCallback(
    (keyword: string) => {
      const stockPlace = getStockPlaceByStoreId(createdFrom);
      const stockPlaceList = stockPlace ? [stockPlace] : undefined;

      return {
        keyword,
        pageNum: 1,
        pageSize: 10,
        transformPriceToJpyFlag: 0,
        stockStatuses: [PMS_PRODUCT_STOCK_STATUS.FOR_SALE],
        stockPlaceList,
      };
    },
    [createdFrom],
  );

  const handleAddProduct = useCallback((product: PmsProduct) => {
    setProductList((prev) => {
      let rejectReason: 'duplicate' | 'stockPlace' | null = null;
      if (prev.find((i) => i.id === product.id)) {
        rejectReason = 'duplicate';
      }

      if (!prev.every((d) => d.stockPlace === product.stockPlace)) {
        rejectReason = 'stockPlace';
      }

      if (rejectReason) {
        if (rejectReason === 'duplicate') {
          message.warning({
            content: '商品已添加',
            key: 'duplicateProduct',
          });
        }

        if (rejectReason === 'stockPlace') {
          message.warning({
            content: '有商品不属于同一产地，无法计算价格',
            key: 'stockPlace',
          });
        }

        return prev;
      }

      return [...prev, handleProductPriceType(product)];
    });

    setProductOptionList([]);
  }, []);

  const fetchProductByKeyword = useCallback(
    async (keyword: string) => {
      keyword = keyword.trim();
      if (!keyword) return undefined;

      const { data } = await getProductList(getProductSearchParams(keyword));
      return resolveScannedProduct(keyword, data.list);
    },
    [getProductSearchParams],
  );

  const debounceHandleSearchProduct = useMemo(() => {
    return debounce((keyword: string) => {
      keyword = keyword.trim();
      if (!keyword) {
        setProductOptionList([]);
        return;
      }

      toggleLoading(true);

      getProductList(getProductSearchParams(keyword))
        .then((data) => {
          setProductOptionList(data.data.list);
        })
        .catch()
        .finally(() => toggleLoading(false));
    }, 300);
  }, [getProductSearchParams, toggleLoading]);

  const handleSearchProduct = useCallback(
    (keyword: string) => {
      debounceHandleSearchProduct(keyword);
    },
    [debounceHandleSearchProduct],
  );

  const handleProductScanSuccess = useMemo(() => {
    return throttle(
      async (text: string) => {
        const keyword = text.trim();
        if (!keyword) return;

        toggleLoading(true);
        try {
          const product = await fetchProductByKeyword(keyword);
          if (!product) {
            message.error({
              content: '未找到商品',
              key: 'productNotFound',
            });
            return;
          }

          handleAddProduct(product);
        } catch {
          message.error({
            content: '查询商品失败',
            key: 'productSearchFailed',
          });
        } finally {
          toggleLoading(false);
        }
      },
      1000,
      { leading: true, trailing: false },
    );
  }, [fetchProductByKeyword, handleAddProduct, toggleLoading]);

  return (
    <div>
      {/* 会员选择 */}
      <UmsMemberSelect />

      {/* 商品选择 */}
      <div>
        <h2>{i18n.t(LOCALS.product_selection)}</h2>
        <div className="flex items-center">
          <div className="flex w-full gap-2">
            <Select
              placeholder={i18n.t(LOCALS.product_sn) || '商品编号'}
              className="flex-1"
              filterOption={false}
              notFoundContent={loading ? <Spin size="small" /> : null}
              onSearch={handleSearchProduct}
              value={null}
              showSearch
              onChange={(pmsProductId: PmsProduct['id']) => {
                const product = productOptionList.find(
                  (i) => i.id === pmsProductId,
                );
                if (product) {
                  handleAddProduct(product);
                }
              }}
              options={productOptionList.map((i) => {
                return {
                  ...i,
                  value: i.id,
                  label: i.productSn,
                };
              })}
              // @ts-ignore
              optionRender={({
                data: {
                  id,
                  price,
                  currency,
                  stockPlace,
                  publishStatus,
                  stock,
                  stockStatus,
                },
              }: {
                data: PmsProduct;
              }) => {
                return (
                  <div className="flex items-center">
                    <span className="mr-2">{id}</span>
                    <span className="mr-2">{`${currency} ${thousands(
                      price,
                    )}`}</span>
                    <span className="mr-2">
                      {findLabelByValue(stockPlace, STOCK_PLACE_OPTION_LIST)}
                    </span>
                    <span className="mr-2">
                      {findLabelByValue(
                        stockStatus,
                        PMS_PRODUCT_STOCK_STATUS_OPTION_LIST,
                      )}
                    </span>
                    {findLabelByValue(
                      publishStatus,
                      PUBLISH_STATUS_OPTION_LIST,
                    )}
                    {` / ${stock}`}
                  </div>
                );
              }}
            />
            <Button
              icon={<ScanOutlined />}
              onClick={toggleProductScannerOpen}
            />
            <Modal
              destroyOnClose
              open={productScannerOpen}
              onCancel={toggleProductScannerOpen}
              footer={null}
              title={i18n.t(LOCALS.product_selection)}
            >
              <BarcodeScanner onScanSuccess={handleProductScanSuccess} />
            </Modal>
          </div>

          <div className="ml-4 flex shrink-0 items-center">
            {i18n.t(LOCALS.shop)}：
            <Select
              disabled={allShopIdList.length === 1}
              value={state.createdFrom}
              className="w-48"
              options={
                allShopIdList.length
                  ? SHOP_OPTION_LIST.filter((shop) =>
                      allShopIdList.includes(shop.value),
                    )
                  : SHOP_OPTION_LIST
              }
              onChange={(e) => {
                localStorage.setItem(
                  CHECKOUT_COUNTER_CACHE_SHOP_ID_KEY,
                  `${e}`,
                );
                dispatch({
                  type: ActionType.UPDATE_BATCH,
                  payload: {
                    createdFrom: e,
                  },
                });
                setTimeout(() => {
                  window.location.reload();
                }, 300);
              }}
            ></Select>
          </div>

          <div className="ml-4 flex shrink-0 items-center">
            {i18n.t(LOCALS.staff)}：
            <Select
              value={state.staffName}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '').includes(input)
              }
              className="w-32"
              options={staffSelectOptions
                .filter((i) => i.status === 1)
                .map((d) => ({
                  value: d.label,
                  label: d.label,
                }))}
              onChange={(e) => {
                dispatch({
                  type: ActionType.UPDATE_BATCH,
                  payload: {
                    staffName: e,
                  },
                });
              }}
            ></Select>
          </div>
        </div>
        {/* 商品表格 */}
        <ProductTable
          orderResult={orderResult}
          loading={loadingProductList}
          productList={productList}
          mode="list"
          onChange={(product) => {
            const newProductList = produce(productList, (draft) => {
              const targetProduct = draft.find((i) => i.id === product.id);

              if (targetProduct) {
                targetProduct.priceType = product.priceType;
                targetProduct.quantity = product.quantity;
              }
            });
            setProductList(newProductList);
          }}
          onDelete={(product) => {
            setProductList(productList.filter((i) => i.id !== product.id));
          }}
        />
      </div>

      {/* 支付细节 */}
      <PaymentInfo
        orderResult={orderResult}
        setOrderResult={setOrderResult}
        productList={productList}
      />
    </div>
  );
};

const OmsCheckoutCounterWarp = () => (
  <PayloadContextProvider>
    <OmsCheckoutCounter />
  </PayloadContextProvider>
);
export default OmsCheckoutCounterWarp;
